import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

/**
 * One setting of the SuPAR app, as the backend's catalogue describes it. `default` is the
 * app's own built-in value — what applies where neither the organization nor a team set
 * anything, shown for reference and not editable from here.
 */
interface SettingBase {
  key: string;
  /** The setting only takes effect while another holds this value. A hint, never a lock. */
  appliesWhen?: { key: string; value: string | boolean };
}

export type AppSettingDefinition = SettingBase & (
  | { type: 'boolean'; default: boolean }
  | { type: 'choice'; options: string[]; default: string }
  | { type: 'color'; options: string[]; default: string }
  | { type: 'lineWidth'; options: number[]; default: number }
  | { type: 'range'; min: number; max: number; step: number; format: 'percent' | 'factor'; default: number }
  /** A language tag, or `''` for the device's own language. */
  | { type: 'language'; default: string }
);

export type AppSettingValue = boolean | string | number;

export interface AppSettingGroup {
  kind: 'group';
  id: string;
  fields: AppSettingDefinition[];
}

/** One entry of the catalogue: a group of settings, or a setting on its own. */
type AppSettingsCatalogueItem = AppSettingGroup | (AppSettingDefinition & { kind: 'field' });

/** Settings keyed flat by their dotted key — how this client works with them. */
export type AppSettingValues = Record<string, AppSettingValue>;

/**
 * Settings nested by key path — how the API carries them. Never flat dotted keys on the way
 * out: the backend's request sanitizer deletes every body key containing a dot.
 */
interface NestedValues {
  [segment: string]: NestedValues | AppSettingValue | null;
}

/** One team's settings, or the organization's, whose `teamId` is `null`. */
interface AppSettingsEntry {
  teamId: string | null;
  /** Only what was set here. */
  values: AppSettingValues;
  updatedAt: string | null;
}

interface AppSettingsEntryResponse extends Omit<AppSettingsEntry, 'values'> {
  values: NestedValues;
}

/** Everything the reader may open: the organization's settings, and each team's by team id. */
interface AllAppSettings {
  organization: AppSettingsEntry;
  teams: Record<string, AppSettingsEntry>;
}

const flatten = (nested: NestedValues, prefix = ''): AppSettingValues => {
  const values: AppSettingValues = {};

  for (const [segment, value] of Object.entries(nested)) {
    if (value !== null && typeof value === 'object') {
      Object.assign(values, flatten(value, `${prefix}${segment}.`));
    } else if (value !== null) {
      values[`${prefix}${segment}`] = value;
    }
  }

  return values;
};

const nest = (values: Record<string, AppSettingValue | null>): NestedValues => {
  const nested: NestedValues = {};

  for (const [key, value] of Object.entries(values)) {
    const segments = key.split('.');
    let parent = nested;

    for (const segment of segments.slice(0, -1)) {
      const child = parent[segment];

      if (child !== null && typeof child === 'object') {
        parent = child;
      } else {
        const created: NestedValues = {};
        parent[segment] = created;
        parent = created;
      }
    }

    parent[segments[segments.length - 1]] = value;
  }

  return nested;
};

const toEntry = (response: AppSettingsEntryResponse): AppSettingsEntry => ({
  ...response, values: flatten(response.values),
});

/** The API's one list, the organization's entry being the one with no team, split by what it holds. */
const toAllAppSettings = (response: AppSettingsEntryResponse[]): AllAppSettings => {
  const all: AllAppSettings = { organization: { teamId: null, values: {}, updatedAt: null }, teams: {} };

  for (const entry of response) {
    if (entry.teamId === null) all.organization = toEntry(entry);
    else all.teams[entry.teamId] = toEntry(entry);
  }

  return all;
};

const entryOf = (all: AllAppSettings, teamId: string | null): AppSettingsEntry => {
  if (teamId === null) return all.organization;

  // A team created since the list was read has no entry yet.
  all.teams[teamId] ??= { teamId, values: {}, updatedAt: null };

  return all.teams[teamId];
};

/** A value sets the setting; `null` takes it off so what is beneath applies again. */
export interface PatchAppSettingsInput {
  /** The team written to, or `null` for the organization. */
  teamId: string | null;
  values: Record<string, AppSettingValue | null>;
}

// Writes still on their way, counted per team (the organization under ''), and the ones whose
// answers cannot be taken for the server's state once they are all through.
const pendingWrites = new Map<string, number>();
const outOfStep = new Set<string>();

export const appSettingsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAppSettingsSchema: builder.query<AppSettingsCatalogueItem[], void>({
      query: () => '/v1/app-settings/schema',
      onQueryStarted: handleApiResult(),
    }),
    /** The organization's settings and every team's the reader may open, so moving between them asks for nothing. */
    getAppSettings: builder.query<AllAppSettings, void>({
      query: () => '/v1/app-settings',
      transformResponse: toAllAppSettings,
      providesTags: ['AppSettings'],
      onQueryStarted: handleApiResult(),
    }),
    /**
     * Save sends every unsaved change of one team, or of the organization, as one patch. Each
     * setting is written on its own server-side, so two people editing different settings
     * never undo each other and no version is claimed.
     *
     * Nothing goes into the list ahead of the answer: the page shows its draft until then, so
     * a write that is refused, or that never reaches the server, leaves the list as it was
     * read and the draft unsaved. Putting the values in first left them there as saved when
     * the write and the re-read after it both failed.
     *
     * The answer — the settings after that write — replaces the entry only when the write ran
     * alone: answers of writes that overlap may land in either order and each lacks what the
     * other set, and so may a re-read of the list that was under way when the write began.
     * Those put in only what they sent, and once they are all through the list is read again.
     */
    patchAppSettings: builder.mutation<AppSettingsEntry, PatchAppSettingsInput>({
      query: ({ teamId, values }) => ({
        url: teamId === null ? '/v1/app-settings/organization' : `/v1/app-settings/teams/${teamId}`,
        method: 'PATCH',
        body: { values: nest(values) },
      }),
      transformResponse: toEntry,
      onQueryStarted: async (input, api) => {
        const { teamId, values } = input;
        const target = teamId ?? '';

        const pending = (pendingWrites.get(target) ?? 0) + 1;
        pendingWrites.set(target, pending);

        const isListBeingRead = appSettingsApiSlice.endpoints.getAppSettings.select()(api.getState()).status === 'pending';
        if (pending > 1 || isListBeingRead) outOfStep.add(target);

        await handleApiResult()(input, api);

        let answer: AppSettingsEntry | undefined;

        try {
          answer = (await api.queryFulfilled).data;
        } catch (_) {
          // Refused, or never arrived: nothing was written, there or here.
        }

        const left = (pendingWrites.get(target) ?? 1) - 1;

        if (left > 0) pendingWrites.set(target, left);
        else pendingWrites.delete(target);

        const isAlone = left === 0 && !outOfStep.has(target);

        if (answer) {
          const settled = answer;

          api.dispatch(appSettingsApiSlice.util.updateQueryData('getAppSettings', undefined, (all) => {
            if (isAlone) {
              if (teamId === null) all.organization = settled;
              else all.teams[teamId] = settled;

              return;
            }

            const entry = entryOf(all, teamId);

            for (const [key, value] of Object.entries(values)) {
              if (value === null) delete entry.values[key];
              else entry.values[key] = value;
            }
          }));
        }

        if (left === 0 && outOfStep.delete(target)) {
          api.dispatch(appSettingsApiSlice.util.invalidateTags(['AppSettings']));
        }
      },
    }),
  }),
});

export const {
  useGetAppSettingsQuery,
  useGetAppSettingsSchemaQuery,
  usePatchAppSettingsMutation,
} = appSettingsApiSlice;
